"use client";

import { ChartBar as BarChart3, ShieldCheck, Zap, ArrowRight } from 'lucide-react';
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: cardsRef,
    isVisible: cardsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const pillars = [{
    index: "§ 01.1",
    icon: BarChart3,
    title: "Dynamic",
    italicWord: "Pricing",
    titleSuffix: "",
    description: "Leverage real-time market data to adjust your rates automatically, ensuring you capture maximum revenue during high-demand periods."
  }, {
    index: "§ 01.2",
    icon: ShieldCheck,
    title: "Rate",
    italicWord: "Parity",
    titleSuffix: "",
    description: "Protect your brand integrity and direct booking strategy by monitoring and correcting discrepancies across all OTA channels instantly."
  }, {
    index: "§ 01.3",
    icon: Zap,
    title: "Competitor",
    italicWord: "Intelligence",
    titleSuffix: "",
    description: "Gain a decisive edge with deep insights into competitor pricing trends, allowing you to pivot your strategy before the market shifts."
  }];
  return <section data-section-id="2110" id="ratemonitoringtechnology" className="relative overflow-hidden bg-slate-50 py-28 md:py-36">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute right-[-4%] top-[2%] hidden select-none md:block" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "clamp(320px, 52vw, 760px)",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px hsl(222 47% 7%)",
      opacity: 0.03,
      fontWeight: 700
    }}>
        01
      </div>
      <div aria-hidden="true" className="pointer-events-none absolute right-[-8%] top-[1%] block select-none md:hidden" style={{
      fontFamily: "var(--font-serif)",
      fontSize: "320px",
      lineHeight: 0.8,
      color: "transparent",
      WebkitTextStroke: "1px hsl(222 47% 7%)",
      opacity: 0.03,
      fontWeight: 700
    }}>
        01
      </div>

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-8 lg:px-14">
        {}
        <div ref={headerRef} className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-6 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            {}
            <div className="flex items-center gap-2 uppercase" style={{
            fontFamily: "var(--font-sans)",
            fontSize: "0.6875rem",
            fontWeight: 600,
            letterSpacing: "0.18em",
            lineHeight: 1,
            color: "var(--secondary)"
          }}>
              <span>§ 01</span>
              <span className="text-slate-300">—</span>
              <span className="text-slate-500">REVENUE TECHNOLOGY / CORE SOLUTIONS</span>
            </div>

            {}
            <div className="mt-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <div className="flex items-center gap-1">
                <span className="h-1 w-1 rounded-lg bg-secondary" />
                <span className="h-1 w-1 rounded-lg bg-secondary" />
                <span className="h-1 w-1 rounded-lg bg-secondary" />
              </div>
            </div>

            {}
            <h2 className="mt-8 font-default" style={{
            fontSize: "clamp(2.25rem, 4.8vw, 4.5rem)",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            lineHeight: 1.1,
            color: "var(--primary)"
          }}>
              Advanced revenue{" "}
              <br className="hidden sm:block" />
              monitoring{" "}
              <em className="font-default" style={{
              fontStyle: "italic",
              fontWeight: 400,
              color: "var(--secondary)"
            }}>
                systems
              </em>
              .
            </h2>
          </div>

          <div className="lg:col-span-5 lg:flex lg:items-end">
            <p className="font-default max-w-md text-slate-700" style={{
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            lineHeight: 1.65
          }}>
              HotelRevBaBa integrates cutting-edge revenue technology to provide you with actionable data, ensuring your property stays ahead in a competitive hospitality landscape.
            </p>
          </div>
        </div>

        {}
        <div ref={cardsRef} className="mt-20 grid grid-cols-1 gap-6 md:gap-7 lg:grid-cols-12 lg:gap-6">
          {pillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <div key={i} data-index={i} className={`lg:col-span-4 transition-all ease-out ${cardsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            transitionDuration: "720ms",
            transitionDelay: `${i * 80}ms`
          }}>
                <Card className="group relative h-full overflow-hidden p-9 transition-transform duration-300 ease-out hover:-translate-y-1 shadow-md" style={{
              backgroundColor: "white",
              borderRadius: "0.5rem",
              border: "1px solid var(--border)"
            }}>
                  {}
                  <div className="flex items-center gap-2 uppercase" style={{
                fontFamily: "var(--font-sans)",
                fontSize: "0.6875rem",
                fontWeight: 600,
                letterSpacing: "0.18em",
                lineHeight: 1,
                color: "var(--secondary)"
              }}>
                    <span>{pillar.index}</span>
                    <span className="text-slate-200">—</span>
                    <span className="text-slate-400">SOLUTION</span>
                  </div>

                  {}
                  <div className="mt-8 transition-transform duration-[400ms] ease-out group-hover:scale-110">
                    <Icon size={32} strokeWidth={1.5} className="text-secondary" />
                  </div>

                  {}
                  <h3 className="mt-7 font-default" style={{
                fontSize: "clamp(1.5rem, 2.2vw, 2.25rem)",
                fontWeight: 700,
                letterSpacing: "-0.012em",
                lineHeight: 1.2,
                color: "var(--primary)"
              }}>
                    {pillar.title}{" "}
                    <em className="font-default" style={{
                  fontStyle: "italic",
                  fontWeight: 400,
                  color: "var(--secondary)"
                }}>
                      {pillar.italicWord}
                    </em>
                  </h3>

                  {}
                  <p className="font-default mt-5 text-slate-600" style={{
                fontSize: "1rem",
                lineHeight: 1.65
              }}>
                    {pillar.description}
                  </p>

                  {}
                  <div className="mt-9 flex items-center gap-3">
                    <div className="h-px flex-1 bg-slate-100" />
                    <div className="flex items-center gap-1">
                      <span className="h-1 w-1 rounded-lg bg-secondary" />
                      <span className="h-1 w-1 rounded-lg bg-secondary" />
                      <span className="h-1 w-1 rounded-lg bg-secondary" />
                    </div>
                  </div>

                  {}
                  <div className="mt-6">
                    <button type="button" className="group/link relative inline-flex items-center gap-2 font-default" style={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: "var(--secondary)"
                }}>
                      <span className="relative">
                        Learn more
                        <span className="absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-300 ease-out group-hover/link:w-full bg-secondary" />
                      </span>
                      <ArrowRight size={16} strokeWidth={2} className="transition-transform duration-300 group-hover/link:translate-x-1" />
                    </button>
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}